import { User, Save, X } from 'lucide-react'
import { useState, useEffect } from 'react'

import { Button } from '@/components/ui/button'
import CollapsibleSection from '@/components/ui/CollapsibleSection'
import { Input } from '@/components/ui/input'
import { useTravelRule, useUpdateTravelRule } from '@/hooks/useCpn'
import { CpnTravelRule } from '@/types/cpn'
import { logger } from '@/utils/logger'
import { emailRegex } from '@/utils/validation'

interface TravelRuleFormData {
  // Originator fields
  ORIGINATOR_NAME?: string
  ORIGINATOR_NATIONALITY?: string
  ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER?: string
  ORIGINATOR_ACCOUNT_NUMBER?: string
  ORIGINATOR_ADDRESS?: string
  ORIGINATOR_FINANCIAL_INSTITUTION_NAME?: string
  ORIGINATOR_FINANCIAL_INSTITUTION_ADDRESS?: string
  // Beneficiary fields
  BENEFICIARY_NAME?: string
  BENEFICIARY_ADDRESS?: string
  BENEFICIARY_DATE_OF_BIRTH?: string
  BENEFICIARY_NATIONALITY?: string
  BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER?: string
  BENEFICIARY_PHONE_NUMBER?: string
  BENEFICIARY_EMAIL?: string
  // Other fields
  RECIPIENT_PHONE_NUMBER?: string
  RECIPIENT_ID_NUMBER?: string
  RECIPIENT_LEGAL_NAME?: string
  RECIPIENT_CITY?: string
}

interface FormErrors {
  [key: string]: string | undefined
}

interface SenderEditDrawerProps {
  isOpen: boolean
  onClose: () => void
  missingRequirements: CpnTravelRule[]
  beneficiaryUuid?: string
  onSenderUpdated?: () => void
}

const SenderEditDrawer = ({
  isOpen,
  onClose,
  missingRequirements,
  beneficiaryUuid,
  onSenderUpdated,
}: SenderEditDrawerProps) => {
  const { data: travelRuleData, isLoading: isTravelRuleLoading } =
    useTravelRule(isOpen)
  const updateTravelRuleMutation = useUpdateTravelRule()

  const [formData, setFormData] = useState<TravelRuleFormData>({})
  const [formErrors, setFormErrors] = useState<FormErrors>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [isUpdating, setIsUpdating] = useState(false)

  // Initialize form data from travel rule data
  useEffect(() => {
    if (travelRuleData?.metadata) {
      const initialData: TravelRuleFormData = {}
      missingRequirements.forEach((req) => {
        initialData[req] = (travelRuleData.metadata?.[req] as string) || ''
      })
      setFormData(initialData)
    }
  }, [travelRuleData, missingRequirements])

  // Validation functions
  const validateField = (name: string, value: string): string | undefined => {
    const trimmedValue = value.trim()

    switch (name) {
      case 'ORIGINATOR_NAME':
      case 'BENEFICIARY_NAME':
      case 'RECIPIENT_LEGAL_NAME':
        if (!trimmedValue) return 'Name is required'
        if (trimmedValue.length < 2) return 'Name must be at least 2 characters'
        if (trimmedValue.length > 100)
          return 'Name must be less than 100 characters'
        if (!/^[A-Za-z\s'-]+$/.test(trimmedValue))
          return 'Name can only contain letters, spaces, hyphens, and apostrophes'
        break

      case 'ORIGINATOR_NATIONALITY':
      case 'BENEFICIARY_NATIONALITY':
        if (!trimmedValue) return 'Nationality is required'
        if (value.length !== 2)
          return 'Nationality must be 2-letter country code (e.g., CA, US)'
        if (!/^[A-Z]{2}$/.test(value))
          return 'Nationality code must be uppercase letters'
        break

      case 'ORIGINATOR_ADDRESS':
      case 'BENEFICIARY_ADDRESS':
      case 'ORIGINATOR_FINANCIAL_INSTITUTION_ADDRESS':
        if (!trimmedValue) return 'Address is required'
        if (trimmedValue.length < 10)
          return 'Address must be at least 10 characters'
        if (trimmedValue.length > 200)
          return 'Address must be less than 200 characters'
        break

      case 'RECIPIENT_CITY':
        if (!trimmedValue) return 'City is required'
        if (trimmedValue.length < 2) return 'City must be at least 2 characters'
        if (trimmedValue.length > 50)
          return 'City must be less than 50 characters'
        break

      case 'BENEFICIARY_DATE_OF_BIRTH': {
        if (!trimmedValue) return 'Date of birth is required'
        // Validate YYYY-MM-DD format
        if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmedValue))
          return 'Date must be in YYYY-MM-DD format'
        const date = new Date(trimmedValue)
        if (isNaN(date.getTime())) return 'Invalid date'
        if (date > new Date()) return 'Date of birth cannot be in the future'
        const minDate = new Date()
        minDate.setFullYear(minDate.getFullYear() - 120)
        if (date < minDate) return 'Date of birth is too far in the past'
        break
      }

      case 'ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER':
      case 'BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER':
      case 'RECIPIENT_ID_NUMBER':
        if (!trimmedValue) return 'ID number is required'
        if (trimmedValue.length < 5)
          return 'ID number must be at least 5 characters'
        if (trimmedValue.length > 50)
          return 'ID number must be less than 50 characters'
        if (!/^[A-Za-z0-9-]+$/.test(trimmedValue))
          return 'ID number can only contain letters, numbers, and hyphens'
        break

      case 'ORIGINATOR_ACCOUNT_NUMBER':
        if (!trimmedValue) return 'Account number is required'
        if (trimmedValue.length < 5)
          return 'Account number must be at least 5 characters'
        if (trimmedValue.length > 50)
          return 'Account number must be less than 50 characters'
        if (!/^[A-Za-z0-9-]+$/.test(trimmedValue))
          return 'Account number can only contain letters, numbers, and hyphens'
        break

      case 'ORIGINATOR_FINANCIAL_INSTITUTION_NAME':
        if (!trimmedValue) return 'Bank name is required'
        if (trimmedValue.length < 2)
          return 'Bank name must be at least 2 characters'
        if (trimmedValue.length > 100)
          return 'Bank name must be less than 100 characters'
        break

      case 'BENEFICIARY_PHONE_NUMBER':
      case 'RECIPIENT_PHONE_NUMBER':
        if (!trimmedValue) return 'Phone number is required'
        // Allow formats like +1234567890 or 1234567890
        if (!/^\+?\d{10,15}$/.test(trimmedValue.replace(/[\s-]/g, ''))) {
          return 'Phone number must be 10-15 digits (optionally with + prefix)'
        }
        break

      case 'BENEFICIARY_EMAIL':
        if (!trimmedValue) return 'Email is required'
        if (!emailRegex.test(trimmedValue)) return 'Invalid email format'
        if (trimmedValue.length > 100)
          return 'Email must be less than 100 characters'
        break
    }
    return undefined
  }

  // Form handlers
  const handleChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }))

    if (touched[name]) {
      const error = validateField(name, value)
      setFormErrors((prev) => ({ ...prev, [name]: error }))
    }
  }

  const handleInputBlur = (name: string) => {
    setTouched((prev) => ({ ...prev, [name]: true }))
    const error = validateField(
      name,
      formData[name as keyof TravelRuleFormData] || ''
    )
    setFormErrors((prev) => ({ ...prev, [name]: error }))
  }

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}
    let isValid = true

    missingRequirements.forEach((req) => {
      const value = formData[req] || ''
      const error = validateField(req, value)
      if (error) {
        newErrors[req] = error
        isValid = false
      }
    })

    setFormErrors(newErrors)
    setTouched(
      missingRequirements.reduce((acc, req) => ({ ...acc, [req]: true }), {})
    )
    return isValid
  }

  const handleSave = async () => {
    if (!validateForm()) {
      return
    }

    setIsUpdating(true)

    try {
      // Filter out empty values and prepare metadata
      const metadata = Object.fromEntries(
        Object.entries(formData).filter(([_, value]) => value?.trim() !== '')
      ) as Record<string, string>

      // Check if we have any beneficiary fields that require beneficiaryUuid
      const beneficiaryFieldsInMetadata = [
        'BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER',
      ]
      const hasBeneficiaryFields = beneficiaryFieldsInMetadata.some(
        (field) => field in metadata
      )

      await updateTravelRuleMutation.mutateAsync({
        metadata,
        beneficiaryUuid: hasBeneficiaryFields ? beneficiaryUuid : undefined,
      })

      onClose()

      // Notify parent component that sender info was updated
      if (onSenderUpdated) {
        onSenderUpdated()
      }
    } catch (error) {
      logger.error('Failed to update travel rule', error)
      alert('Failed to update sender information. Please try again.')
    } finally {
      setIsUpdating(false)
    }
  }

  const getFieldLabel = (field: string): string => {
    const labels: Record<string, string> = {
      // Originator (Sender) fields
      ORIGINATOR_NAME: 'Your Full Name',
      ORIGINATOR_NATIONALITY: 'Your Nationality',
      ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER: 'Your National ID',
      ORIGINATOR_ACCOUNT_NUMBER: 'Your Account Number',
      ORIGINATOR_ADDRESS: 'Your Address',
      ORIGINATOR_FINANCIAL_INSTITUTION_NAME: 'Your Bank Name',
      ORIGINATOR_FINANCIAL_INSTITUTION_ADDRESS: 'Your Bank Address',
      // Beneficiary (Recipient) fields
      BENEFICIARY_NAME: 'Recipient Full Name',
      BENEFICIARY_ADDRESS: 'Recipient Address',
      BENEFICIARY_DATE_OF_BIRTH: 'Recipient Date of Birth',
      BENEFICIARY_NATIONALITY: 'Recipient Nationality',
      BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER: 'Recipient National ID',
      BENEFICIARY_PHONE_NUMBER: 'Recipient Phone Number',
      BENEFICIARY_EMAIL: 'Recipient Email',
      // Other fields
      RECIPIENT_PHONE_NUMBER: 'Phone Number for Transfer',
      RECIPIENT_ID_NUMBER: 'Recipient ID Number',
      RECIPIENT_LEGAL_NAME: 'Legal Name',
      RECIPIENT_CITY: 'City',
    }
    return labels[field] || field
  }

  const getFieldPlaceholder = (field: string): string => {
    const placeholders: Record<string, string> = {
      // Originator fields
      ORIGINATOR_NAME: 'John Doe',
      ORIGINATOR_NATIONALITY: 'CA',
      ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER:
        'e.g., National ID or Passport',
      ORIGINATOR_ACCOUNT_NUMBER: 'Enter your account number',
      ORIGINATOR_ADDRESS: 'Your full address',
      ORIGINATOR_FINANCIAL_INSTITUTION_NAME: 'Your bank name',
      ORIGINATOR_FINANCIAL_INSTITUTION_ADDRESS: 'Your bank address',
      // Beneficiary fields
      BENEFICIARY_NAME: 'Jane Smith',
      BENEFICIARY_ADDRESS: 'Recipient full address',
      BENEFICIARY_DATE_OF_BIRTH: 'YYYY-MM-DD',
      BENEFICIARY_NATIONALITY: 'HK',
      BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER:
        'e.g., National ID or Passport',
      BENEFICIARY_PHONE_NUMBER: '+852 1234 5678',
      BENEFICIARY_EMAIL: 'recipient@example.com',
      // Other fields
      RECIPIENT_PHONE_NUMBER: '+852 1234 5678',
      RECIPIENT_ID_NUMBER: 'Enter ID number',
      RECIPIENT_LEGAL_NAME: 'Legal name as registered',
      RECIPIENT_CITY: 'City name',
    }
    return placeholders[field] || ''
  }

  const getFieldMaxLength = (field: string): number | undefined => {
    const maxLengths: Record<string, number> = {
      // Originator fields
      ORIGINATOR_NAME: 100,
      ORIGINATOR_NATIONALITY: 2,
      ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER: 50,
      ORIGINATOR_ACCOUNT_NUMBER: 50,
      ORIGINATOR_ADDRESS: 200,
      ORIGINATOR_FINANCIAL_INSTITUTION_NAME: 100,
      ORIGINATOR_FINANCIAL_INSTITUTION_ADDRESS: 200,
      // Beneficiary fields
      BENEFICIARY_NAME: 100,
      BENEFICIARY_ADDRESS: 200,
      BENEFICIARY_NATIONALITY: 2,
      BENEFICIARY_DATE_OF_BIRTH: 10,
      BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER: 50,
      BENEFICIARY_PHONE_NUMBER: 20,
      BENEFICIARY_EMAIL: 100,
      // Other fields
      RECIPIENT_PHONE_NUMBER: 20,
      RECIPIENT_ID_NUMBER: 50,
      RECIPIENT_LEGAL_NAME: 100,
      RECIPIENT_CITY: 50,
    }
    return maxLengths[field]
  }

  const getFieldType = (field: string): string => {
    if (field === 'BENEFICIARY_DATE_OF_BIRTH') return 'date'
    if (field === 'BENEFICIARY_EMAIL') return 'email'
    if (field === 'BENEFICIARY_PHONE_NUMBER') return 'tel'
    return 'text'
  }

  if (!isOpen) return null

  // Group requirements into originator and beneficiary
  const originatorFields = missingRequirements.filter((req) =>
    req.startsWith('ORIGINATOR_')
  )
  const beneficiaryFields = missingRequirements.filter((req) =>
    req.startsWith('BENEFICIARY_')
  )

  // Check if there are any errors in originator or beneficiary fields
  const hasOriginatorErrors = originatorFields.some(
    (field) => formErrors[field]
  )
  const hasBeneficiaryErrors = beneficiaryFields.some(
    (field) => formErrors[field]
  )

  return (
    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-t-lg w-full max-h-[80%] flex flex-col border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="border-b border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-800">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              Update Personal Information
            </h3>
            <Button variant="ghost" size="sm" className="p-1" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 text-left">
            Provide missing information to complete this payment
          </p>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {isTravelRuleLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {/* Originator Information */}
              {originatorFields.length > 0 && (
                <CollapsibleSection
                  title="Your Information"
                  icon={
                    <User className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  }
                  defaultOpen={true}
                  className="bg-white dark:bg-gray-800"
                  hasErrors={hasOriginatorErrors}
                >
                  <div className="space-y-3 pt-3">
                    {originatorFields.map((field) => (
                      <div key={field}>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {getFieldLabel(field)} *
                        </label>
                        <Input
                          type={getFieldType(field)}
                          value={formData[field] || ''}
                          onChange={(e) =>
                            handleChange(
                              field,
                              field.includes('NATIONALITY')
                                ? e.target.value.toUpperCase()
                                : e.target.value
                            )
                          }
                          onBlur={() => handleInputBlur(field)}
                          placeholder={getFieldPlaceholder(field)}
                          maxLength={getFieldMaxLength(field)}
                          className={
                            formErrors[field]
                              ? 'border-red-500 dark:border-red-400'
                              : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'
                          }
                        />
                        {formErrors[field] && (
                          <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                            {formErrors[field]}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </CollapsibleSection>
              )}

              {/* Beneficiary Information */}
              {beneficiaryFields.length > 0 && (
                <CollapsibleSection
                  title="Recipient Information"
                  icon={
                    <User className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  }
                  defaultOpen={originatorFields.length === 0}
                  className="bg-white dark:bg-gray-800"
                  hasErrors={hasBeneficiaryErrors}
                >
                  <div className="space-y-3 pt-3">
                    {beneficiaryFields.map((field) => (
                      <div key={field}>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {getFieldLabel(field)} *
                        </label>
                        <Input
                          type={getFieldType(field)}
                          value={formData[field] || ''}
                          onChange={(e) =>
                            handleChange(
                              field,
                              field.includes('NATIONALITY')
                                ? e.target.value.toUpperCase()
                                : e.target.value
                            )
                          }
                          onBlur={() => handleInputBlur(field)}
                          placeholder={getFieldPlaceholder(field)}
                          maxLength={getFieldMaxLength(field)}
                          className={
                            formErrors[field]
                              ? 'border-red-500 dark:border-red-400'
                              : 'dark:bg-gray-900 dark:border-gray-600 dark:text-white'
                          }
                        />
                        {formErrors[field] && (
                          <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                            {formErrors[field]}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </CollapsibleSection>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-800">
          <Button
            onClick={handleSave}
            disabled={
              Object.values(formErrors).some((error) => error) ||
              isUpdating ||
              isTravelRuleLoading
            }
            className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 text-sm font-medium rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUpdating ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Saving Changes...</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <Save className="w-4 h-4" />
                <span>Save & Continue Payment</span>
              </div>
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default SenderEditDrawer
